/**
 * Onboarding Service
 * Manages onboarding completion status using backend flag from authStore
 */

import { useAuthStore } from "../stores/zustand";

class OnboardingService {
  /**
   * Check if user needs onboarding based on their auth status and backend completion flag
   * @param {Object} authData - Authentication data
   * @param {boolean} authData.isPaid - Whether user has paid subscription
   * @param {boolean} authData.isEmailVerified - Whether email is verified
   * @param {string} authData.current_product - User's current product type
   * @param {string} authData.email - User's email address (optional, kept for compatibility)
   * @returns {boolean} True if user needs onboarding
   */
  needsOnboarding(authData) {
    const { isPaid, isEmailVerified, current_product } = authData;

    // Physio users don't need onboarding
    if (
      current_product === "physio_therapist" ||
      current_product === "physio_user"
    ) {
      return false;
    }

    // Only paid and verified users need onboarding
    if (!isPaid || !isEmailVerified) {
      return false;
    }

    // Check backend flag from authStore
    const onboarding_completed = useAuthStore.getState().onboarding_completed;

    return !onboarding_completed;
  }

  /**
   * Check if onboarding is completed from authStore
   * @returns {boolean} True if onboarding is completed
   */
  isOnboardingCompleted() {
    return useAuthStore.getState().onboarding_completed;
  }

  /**
   * Get completed users (kept for compatibility, returns empty array)
   * @deprecated Backend tracks onboarding completion
   * @returns {string[]} Empty array
   */
  getCompletedUsers() {
    return [];
  }

  /**
   * Clear onboarding for user (kept for compatibility, does nothing)
   * @deprecated Backend handles onboarding state
   */
  clearOnboardingForUser() {}

  /**
   * Clear all onboarding data (kept for compatibility, does nothing)
   * @deprecated Backend handles onboarding state
   */
  clearAllOnboardingData() {}
}

export default new OnboardingService();
